import { useEffect, useState } from "react";
import { Download, Monitor } from "lucide-react";
import { api, SCREENSAVER_DOWNLOAD_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";

function formatSize(bytes?: number) {
  if (!bytes) return "";
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

/** Public, shareable page to download the Windows screensaver. */
export default function DownloadPage() {
  const [info, setInfo] = useState<{ available: boolean; size?: number } | null>(null);

  useEffect(() => {
    api
      .appInfo()
      .then(setInfo)
      .catch(() => setInfo({ available: false }));
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <Monitor className="h-7 w-7" />
        </div>
        <h1 className="text-3xl font-bold">Clinic Screensaver for Windows</h1>
        <p className="mt-2 text-muted-foreground">
          Plays your waiting-room slideshow full-screen and keeps running even if the internet drops.
        </p>

        <div className="mt-8">
          {info?.available ? (
            <Button asChild size="lg">
              <a href={SCREENSAVER_DOWNLOAD_URL} download>
                <Download className="mr-2 h-5 w-5" />
                Download for Windows{info.size ? ` (${formatSize(info.size)})` : ""}
              </a>
            </Button>
          ) : (
            <Button size="lg" disabled>
              <Download className="mr-2 h-5 w-5" />
              {info === null ? "Checking…" : "Not available yet"}
            </Button>
          )}
        </div>

        <div className="mt-10 rounded-lg border bg-muted/30 p-5 text-left text-sm">
          <p className="mb-2 font-medium">Install (≈1 minute)</p>
          <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
            <li>Open the downloaded <code>ClinicScreensaver.scr</code> and choose <strong>Install</strong>.</li>
            <li>
              If Windows warns about an unrecognized app, click <strong>More info → Run anyway</strong>{" "}
              (the file just isn&apos;t code-signed).
            </li>
            <li>
              In the Screen Saver window click <strong>Settings…</strong>, paste your kiosk URL, then{" "}
              <strong>Save</strong>.
            </li>
            <li>Set the idle wait time and click <strong>OK</strong>. Use <strong>Preview</strong> to test.</li>
          </ol>
          <p className="mt-3 text-xs text-muted-foreground">
            Requires Windows 10/11. Leave it online for a few minutes on first run so it can cache
            your media for offline playback.
          </p>
        </div>
      </div>
    </div>
  );
}
